import { Autocomplete, Stack, TextField } from "@mui/material";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { countryCodes } from "./countryCodes";

type Props<T extends FieldValues> = {
    name: Path<T>;
    required: boolean;
    control: Control<T>;
};

function getCountryFromPhoneNumber(phoneNumber?: string | null) {
    if (!phoneNumber || phoneNumber.startsWith("+") === false) return undefined;
    return countryCodes
        .filter(country => phoneNumber.startsWith(country.dialCode))
        .sort((a, b) => b.dialCode.length - a.dialCode.length)[0];
}

export default function PhoneNumberField<T extends FieldValues>({ name, required, control }: Props<T>) {
    const { t } = useTranslation("user");
    const {
        field: { onChange, value },
        fieldState: { error },
    } = useController({ name, control });

    const defaultCountry = countryCodes.find(country => country.code === "NL");
    if (defaultCountry === undefined) throw new Error("Default country not found");

    const country = getCountryFromPhoneNumber(value) ?? defaultCountry;
    const phoneNumber = value?.replace(country.dialCode, "");

    return (
        <Stack gap={1} direction="row">
            <Autocomplete
                sx={{ minWidth: "35%" }}
                disableClearable
                options={countryCodes}
                getOptionLabel={option => `${option.flag} ${option.name} (${option.dialCode})`}
                isOptionEqualToValue={(option, value) => option.dialCode === value.dialCode}
                renderInput={params => (
                    <TextField {...params} label={t("userForm.labelPhoneCountry")} required={required} />
                )}
                value={country}
                onChange={(_, newSelectedCountry) => {
                    onChange(newSelectedCountry.dialCode + phoneNumber);
                }}
            />
            <TextField
                sx={{ flexGrow: 1 }}
                name={name}
                label={t("userCreateForm.labelPhone")}
                value={phoneNumber}
                onChange={event => {
                    onChange(country.dialCode + event.target.value);
                }}
                required={required}
                error={error !== undefined}
                helperText={error?.message}
            />
        </Stack>
    );
}
